import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Create as PenIcon,
  Flip as FlipIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import type { PublicUnitForMenu } from '../unit/types';
import FullVersionTooltip, { GATED_DISABLED_SX } from '../FullVersionTooltip';

/**
 * One floor plan in the public compare cart — mirrors the agent's
 * `FloorPlanToCompare` shape used by `FloorPlanComparisonViewer.tsx`.
 */
export interface PublicFloorPlanToCompare {
  unit: PublicUnitForMenu;
  imageUrl: string;
}

/** Quick-reference row data for the expanded compare sidebar. */
export interface CompareQuickRefColumn {
  unit: PublicUnitForMenu;
  priceDisplay: string;
  psfDisplay: string;
  areaDisplay: string;
  bedroomDisplay: string;
  bathroomDisplay: string;
}

interface FloorPlanComparisonViewerProps {
  floorPlans: PublicFloorPlanToCompare[];
  quickRefColumns: CompareQuickRefColumn[];
}

/** Disabled toolbar control with the shared full-version tooltip. */
const GatedToolbarButton: React.FC<{
  label: string;
  icon: React.ReactNode;
}> = ({ label, icon }) => (
  <FullVersionTooltip>
    <span>
      <IconButton size="small" disabled aria-label={label} sx={GATED_DISABLED_SX}>
        {icon}
      </IconButton>
    </span>
  </FullVersionTooltip>
);

/**
 * Public-side floor-plan comparison viewer. Replicated from the agent's
 * expanded compare columns in `SuperPickerApp.tsx` (~line 27318).
 * Mirror / rotate / draw / reorder controls are visible but disabled.
 */
export const FloorPlanComparisonViewer: React.FC<FloorPlanComparisonViewerProps> = ({
  floorPlans,
  quickRefColumns,
}) => {
  const [quickRefOpen, setQuickRefOpen] = useState(true);
  // Side panel on tablet landscape + desktop; bottom bar on phone and tablet portrait.
  const quickRefSideBySide = useMediaQuery('(min-width:600px) and (orientation: landscape), (min-width:1200px)');
  const [focusedUnitKey, setFocusedUnitKey] = useState<string | null>(
    floorPlans[0]?.unit.unitKey ?? null,
  );

  if (floorPlans.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
          No floor plans selected. Add floor plans from the unit menu.
        </Typography>
      </Box>
    );
  }

  const tripleColumnFit = floorPlans.length === 3;

  const renderPlanColumn = (plan: PublicFloorPlanToCompare, index: number) => {
    const isFocused = plan.unit.unitKey === focusedUnitKey;
    return (
      <Box
        key={plan.unit.unitKey}
        sx={{
          flex: '1 1 0',
          minWidth: tripleColumnFit ? 0 : 380,
          maxWidth: tripleColumnFit ? '100%' : undefined,
          border: '1px solid rgba(var(--color-text-rgb), 0.2)',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
          boxShadow: isFocused ? '0 0 0 2px rgba(var(--color-accent-rgb), 0.6)' : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
            {plan.unit.projectName} {plan.unit.unitNumber}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }}>
            <GatedToolbarButton label="Mirror flip" icon={<FlipIcon fontSize="small" />} />
            <GatedToolbarButton label="Rotate left" icon={<RotateLeftIcon fontSize="small" />} />
            <GatedToolbarButton label="Rotate right" icon={<RotateRightIcon fontSize="small" />} />
            <GatedToolbarButton label="Move left" icon={<ArrowBackIcon fontSize="small" />} />
            <GatedToolbarButton label="Move right" icon={<ArrowForwardIcon fontSize="small" />} />
          </Box>
        </Box>
        <Box
          onClick={() => setFocusedUnitKey(plan.unit.unitKey)}
          sx={{
            height: { xs: '50vh', md: '58vh' },
            minHeight: 360,
            p: 0.5,
            cursor: 'grab',
            bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
            overflow: 'hidden',
          }}
        >
          {plan.imageUrl ? (
            <TransformWrapper
              initialScale={1}
              minScale={0.8}
              maxScale={8}
              centerOnInit
              limitToBounds={false}
              wheel={{ step: 0.08 }}
              pinch={{ step: 5 }}
              doubleClick={{ step: 0.8 }}
            >
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={plan.imageUrl}
                  alt={`${plan.unit.projectName} ${plan.unit.unitNumber}`}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </TransformComponent>
            </TransformWrapper>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                No image
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: quickRefSideBySide
          ? (quickRefOpen ? 'minmax(0, 1fr) 200px' : 'minmax(0, 1fr) 36px')
          : '1fr',
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Box sx={{ overflowX: tripleColumnFit ? 'hidden' : 'auto', minWidth: 0, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            width: '100%',
            minWidth: tripleColumnFit ? 0 : Math.max(820, floorPlans.length * 420),
            alignItems: 'stretch',
          }}
        >
          {floorPlans.slice(0, 3).map((plan, index) => renderPlanColumn(plan, index))}
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
          borderRadius: 1,
          p: quickRefOpen ? 1.25 : 0.5,
          maxHeight: quickRefSideBySide ? '74vh' : 'none',
          overflowY: 'auto',
          bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
          overflowX: quickRefSideBySide ? 'hidden' : 'auto',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: quickRefOpen ? 'space-between' : 'center',
            alignItems: 'center',
            mb: quickRefOpen ? 1 : 0,
          }}
        >
          {quickRefOpen && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Quick Reference
            </Typography>
          )}
          <Tooltip title={quickRefOpen ? 'Hide Quick Reference' : 'Show Quick Reference'}>
            <IconButton size="small" onClick={() => setQuickRefOpen((prev) => !prev)}>
              {quickRefOpen ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        {quickRefOpen && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: quickRefSideBySide ? 'column' : 'row',
              gap: 1,
              alignItems: 'stretch',
              overflowX: quickRefSideBySide ? 'visible' : 'auto',
            }}
          >
            {quickRefColumns.map((col) => (
              <Box
                key={`cmp-ref-${col.unit.unitKey}`}
                onClick={() => setFocusedUnitKey(col.unit.unitKey)}
                sx={{
                  flex: quickRefSideBySide ? 'none' : '0 0 auto',
                  width: quickRefSideBySide ? 'auto' : 148,
                  p: 0.85,
                  borderRadius: 1,
                  border: '1px solid rgba(var(--color-text-rgb), 0.14)',
                  cursor: 'pointer',
                  bgcolor:
                    col.unit.unitKey === focusedUnitKey
                      ? 'rgba(var(--color-accent-rgb), 0.14)'
                      : 'transparent',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  {col.unit.unitNumber}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Price: {col.priceDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  PSF: {col.psfDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Area: {col.areaDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Bedroom: {col.bedroomDisplay}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Bathroom: {col.bathroomDisplay}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FloorPlanComparisonViewer;
